import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

// D147 — Live-only Step-Progress je laufendem Job (i/n) für den Shimmer-Balken in der
// JobsList. Quelle ist AUSSCHLIESSLICH der DO-Push (reportProgress, KEIN Statuswechsel) →
// nichts davon wird in der DB/Query persistiert. Ein terminaler Status (done/failed/canceled)
// räumt den Eintrag (Job ist nicht mehr 'processing'); JobItem subscribt per-Job, sodass nur
// das betroffene Item re-rendert. Bewusst getrennt vom uiStore (server-nahe, ephemer).
//
// D147-Amendment (User: „loading ist nicht persistent"): der i/n-Stand wird zusätzlich in
// localStorage gespiegelt, damit ein Seiten-Reload den Balken NICHT auf leer zurücksetzt
// (der Runner pusht erst beim nächsten Knoten — bis dahin wäre der Balken sonst weg). Rein
// CLIENT-seitig: KEIN DB-Feld, KEINE Migration, KEINE Subrequest-Kosten (vgl. D106). Über
// Geräte/Erstbesuch hinweg bleibt es bewusst nicht persistent (das wäre die DB-Variante).

export interface JobProgress {
  index: number; // abgeschlossene Knoten (1..count)
  count: number; // Gesamt-Knoten der Pipeline
}

interface JobProgressState {
  byJob: Record<string, JobProgress>;
  /**
   * Tombstone terminierter Jobs (done/failed/canceled). Die Progress-Pushes sind fire-and-forget
   * (notify-store) und können einen Tick NACH der Terminal-Notice eintreffen — ohne Tombstone
   * re-befüllte ein solcher Spät-Push `byJob` und der Balken blitzte kurz wieder auf (Codex-P2-1).
   * Ein terminaler Job wird per D18 nie wieder 'processing', der Tombstone ist also dauerhaft
   * korrekt; er wächst nur mit fertigen Jobs DIESER Session (UUIDs → vernachlässigbar) und wird
   * BEWUSST NICHT persistiert (s. partialize): nach einem Reload existiert kein Spät-Push aus
   * einem früheren Runner mehr, gegen den er schützen müsste.
   */
  done: Record<string, true>;
  setProgress: (jobId: string, index: number, count: number) => void;
  clearProgress: (jobId: string) => void;
}

/**
 * Backstop gegen unbegrenztes localStorage-Wachstum: ein Job, der beendet wird, WÄHREND der Tab
 * geschlossen ist, bekommt keine Terminal-Notice → sein `byJob`-Eintrag wird nie geräumt und
 * bliebe für immer gespeichert. Sichtbar wird er nie (JobItem rendert nur bei status==='processing'),
 * aber wir deckeln die persistierte Menge auf die zuletzt gesehenen PERSIST_CAP Einträge. Greift
 * nur in pathologischen Fällen; ein gedeckelter aktiver Job heilt beim nächsten Push selbst.
 */
const PERSIST_CAP = 200;
export function capPersistedProgress(byJob: Record<string, JobProgress>): Record<string, JobProgress> {
  const keys = Object.keys(byJob);
  if (keys.length <= PERSIST_CAP) return byJob;
  return Object.fromEntries(keys.slice(-PERSIST_CAP).map((k) => [k, byJob[k] as JobProgress]));
}

/** No-op-Storage für Umgebungen ohne localStorage (SSR, vitest `node`, Privacy-Modus/iframe). */
const memoryStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

function browserStorage(): StateStorage {
  try {
    if (typeof window !== "undefined" && window.localStorage) return window.localStorage;
  } catch {
    /* localStorage kann werfen (geblockt im Privacy-Modus / sandboxed iframe) → Memory-Fallback */
  }
  return memoryStorage;
}

export const useJobProgressStore = create<JobProgressState>()(
  persist(
    (set) => ({
      byJob: {},
      done: {},
      setProgress: (jobId, index, count) =>
        set((s) => {
          // Spät-Push nach Terminierung ignorieren (kein Aufblitzen, Codex-P2-1).
          if (s.done[jobId]) return s;
          // Monoton bei gleicher Pipeline-Länge: ein verspätet (out-of-order) eintreffender Push
          // darf den Balken nicht zurückspringen lassen. Ändert sich `count` (anderer Lauf/Resume
          // mit anderer Knotenzahl), übernimmt der neuere Stand. Identischer Stand → kein Re-Render.
          const prev = s.byJob[jobId];
          if (prev && prev.count === count && prev.index >= index) return s;
          return { byJob: { ...s.byJob, [jobId]: { index, count } } };
        }),
      clearProgress: (jobId) =>
        set((s) => {
          // Tombstone setzen (auch wenn noch kein Eintrag existierte — die Terminal-Notice kann VOR
          // dem letzten Progress-Push ankommen) und den laufenden Eintrag räumen.
          const next = { ...s.byJob };
          delete next[jobId];
          return { byJob: next, done: { ...s.done, [jobId]: true } };
        }),
    }),
    {
      // Nur den i/n-Stand client-seitig spiegeln, damit ein Reload den Balken nicht leert.
      name: "loumy:job-progress:v1",
      version: 1,
      storage: createJSONStorage(() => browserStorage()),
      // NUR `byJob` (gedeckelt) sichern — kein `done` (session-lokal, s. oben), keine Funktionen.
      partialize: (s) => ({ byJob: capPersistedProgress(s.byJob) }),
    },
  ),
);
